import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { journalEntriesService } from '../../services/accounting/journal-entries';
import { chartOfAccountsService } from '../../services/accounting/chart-of-accounts';
import { XMarkIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import LoadingSpinner from '../ui/LoadingSpinner';
import toast from 'react-hot-toast';

const CreateJournalEntryModal = ({ onClose, onSuccess, entry = null }) => {
  const { t, i18n } = useTranslation();
  const { currentCompany, user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [chartOfAccounts, setChartOfAccounts] = useState([]);
  const [validationErrors, setValidationErrors] = useState({});
  const [networkStatus, setNetworkStatus] = useState('online');

  // Form data
  const [formData, setFormData] = useState({
    date: entry?.date || new Date().toISOString().split('T')[0],
    entryType: entry?.entryType || 'manual',
    description: entry?.description || '',
    reference: entry?.reference || '',
    lineItems: entry?.lineItems || [
      { accountId: '', accountCode: '', accountName: '', debit: 0, credit: 0, description: '' },
      { accountId: '', accountCode: '', accountName: '', debit: 0, credit: 0, description: '' }
    ]
  });

  // Network status monitoring
  useEffect(() => {
    const handleOnline = () => setNetworkStatus('online');
    const handleOffline = () => setNetworkStatus('offline');
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Load chart of accounts
  useEffect(() => {
    const loadChartOfAccounts = async () => {
      if (!currentCompany?.id) return;
      
      try {
        setNetworkStatus('loading');
        const accounts = await chartOfAccountsService.getChartOfAccounts(currentCompany.id);
        // Remove duplicates based on accountCode
        const uniqueAccounts = accounts.filter((account, index, self) => 
          index === self.findIndex(a => a.accountCode === account.accountCode)
        );
        setChartOfAccounts(uniqueAccounts);
        setNetworkStatus('online');
        
        // Initialize default chart of accounts if empty
        if (accounts.length === 0) {
          console.log('Initializing default chart of accounts...');
          await chartOfAccountsService.initializeDefaultChartOfAccounts(currentCompany.id, user.uid);
          const newAccounts = await chartOfAccountsService.getChartOfAccounts(currentCompany.id);
          const uniqueNewAccounts = newAccounts.filter((account, index, self) => 
            index === self.findIndex(a => a.accountCode === account.accountCode)
          );
          setChartOfAccounts(uniqueNewAccounts);
        }
      } catch (error) {
        console.error('Error loading chart of accounts:', error);
        setNetworkStatus('error');
        if (error.code === 'unavailable' || error.message.includes('network')) {
          toast.error('Network connection issue. Please check your internet connection.');
        } else {
          toast.error('Failed to load chart of accounts');
        }
      }
    };

    loadChartOfAccounts();
  }, [currentCompany?.id, user?.uid]);

  // Entry types configuration
  const entryTypes = [
    { id: 'manual', name: t('journal.manual', 'Manual Entry'), description: t('journal.manual_desc', 'Manual journal entry') },
    { id: 'adjustment', name: t('journal.adjustment', 'Adjustment'), description: t('journal.adjustment_desc', 'Adjusting entry') },
    { id: 'accrual', name: t('journal.accrual', 'Accrual'), description: t('journal.accrual_desc', 'Accrual entry') },
    { id: 'correction', name: t('journal.correction', 'Correction'), description: t('journal.correction_desc', 'Error correction') },
    { id: 'closing', name: t('journal.closing', 'Closing'), description: t('journal.closing_desc', 'Period closing entry') },
    { id: 'reclassification', name: t('journal.reclassification', 'Reclassification'), description: t('journal.reclassification_desc', 'Reclassification entry') }
  ];

  // Calculate totals
  const totalDebits = formData.lineItems.reduce((sum, item) => sum + (parseFloat(item.debit) || 0), 0);
  const totalCredits = formData.lineItems.reduce((sum, item) => sum + (parseFloat(item.credit) || 0), 0);
  const difference = Math.abs(totalDebits - totalCredits);
  const isBalanced = difference < 0.01;

  // Handle input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear validation error when user starts typing
    if (validationErrors[field]) {
      setValidationErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Handle line item changes
  const handleLineItemChange = (index, field, value) => {
    const newLineItems = [...formData.lineItems];
    
    // Handle account selection
    if (field === 'accountId') {
      const selectedAccount = chartOfAccounts.find(acc => acc.id === value);
      if (selectedAccount) {
        newLineItems[index].accountId = value;
        newLineItems[index].accountCode = selectedAccount.accountCode;
        newLineItems[index].accountName = selectedAccount.accountName;
      }
    } else if (field === 'debit') {
      // Clear credit when entering debit
      newLineItems[index].debit = parseFloat(value) || 0;
      newLineItems[index].credit = 0;
    } else if (field === 'credit') {
      // Clear debit when entering credit
      newLineItems[index].credit = parseFloat(value) || 0;
      newLineItems[index].debit = 0;
    } else {
      newLineItems[index][field] = value;
    }
    
    setFormData(prev => ({ ...prev, lineItems: newLineItems }));
  };

  // Add new line item
  const addLineItem = () => {
    setFormData(prev => ({
      ...prev,
      lineItems: [...prev.lineItems, { 
        accountId: '', 
        accountCode: '', 
        accountName: '', 
        debit: 0, 
        credit: 0, 
        description: '' 
      }]
    }));
  };

  // Remove line item
  const removeLineItem = (index) => {
    if (formData.lineItems.length <= 1) {
      toast.error(t('journal.minimum_one_line', 'At least one line item is required'));
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      lineItems: prev.lineItems.filter((_, i) => i !== index)
    }));
  };

  // Auto-balance helper
  const autoBalance = () => {
    if (isBalanced) return;
    
    const lastLineIndex = formData.lineItems.length - 1;
    const newLineItems = [...formData.lineItems];
    
    if (totalDebits > totalCredits) {
      // Need more credits
      newLineItems[lastLineIndex].credit = totalDebits - (totalCredits - (newLineItems[lastLineIndex].credit || 0));
      newLineItems[lastLineIndex].debit = 0;
    } else {
      // Need more debits
      newLineItems[lastLineIndex].debit = totalCredits - (totalDebits - (newLineItems[lastLineIndex].debit || 0));
      newLineItems[lastLineIndex].credit = 0;
    }
    
    setFormData(prev => ({ ...prev, lineItems: newLineItems }));
  };

  // Validate form
  const validateForm = () => {
    const errors = {};
    
    if (!formData.date) {
      errors.date = t('payment.error.date_required', 'Date is required');
    }
    
    if (!formData.description.trim()) {
      errors.description = t('journal.description_required', 'Description is required');
    }
    
    if (!formData.entryType) {
      errors.entryType = t('journal.entry_type_required', 'Entry type is required');
    }
    
    // Validate line items
    let hasValidLineItems = false;
    formData.lineItems.forEach((item, index) => {
      if (!item.accountId) {
        errors[`lineItem_${index}_account`] = t('expenses.categories.account_required', 'Account is required');
      }
      
      // Line description is now optional - removed validation
      
      if ((item.debit || 0) === 0 && (item.credit || 0) === 0) {
        errors[`lineItem_${index}_amount`] = t('payment.error.amount_required', 'Amount is required');
      } else {
        hasValidLineItems = true;
      }
    });
    
    if (!hasValidLineItems) {
      errors.lineItems = t('journal.valid_line_items_required', 'At least one valid line item is required');
    }
    
    if (!isBalanced) {
      errors.balance = t('journal.entry_not_balanced', 'Entry must be balanced (debits = credits)');
    }
    
    setValidationErrors(errors);
    
    // Enhanced error logging for debugging
    if (Object.keys(errors).length > 0) {
      console.log('Journal Entry Validation Errors:', errors);
      console.log('Form Data:', formData);
      console.log('Total Debits:', totalDebits);
      console.log('Total Credits:', totalCredits);
      console.log('Difference:', difference);
      console.log('Is Balanced:', isBalanced);
    }
    
    return Object.keys(errors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      // Show more specific error messages
      const errorCount = Object.keys(validationErrors).length;
      const errorMessages = [];
      
      if (validationErrors.date) errorMessages.push('Date is required');
      if (validationErrors.description) errorMessages.push('Description is required');
      if (validationErrors.entryType) errorMessages.push('Entry type is required');
      if (validationErrors.balance) errorMessages.push('Entry must be balanced (debits = credits)');
      if (validationErrors.lineItems) errorMessages.push('Valid line items are required');
      
      // Check for line item specific errors
      Object.keys(validationErrors).forEach(key => {
        if (key.includes('lineItem_') && key.includes('_account')) {
          errorMessages.push('All line items must have an account selected');
        }
        if (key.includes('lineItem_') && key.includes('_amount')) {
          errorMessages.push('All line items must have a debit or credit amount');
        }
      });
      
      const uniqueMessages = [...new Set(errorMessages)];
      const errorMessage = uniqueMessages.length > 0 
        ? `Please fix these errors: ${uniqueMessages.join(', ')}`
        : t('journal.fix_errors', 'Please fix the errors and try again');
      
      toast.error(errorMessage);
      console.log('Validation failed with errors:', validationErrors);
      return;
    }
    
    try {
      setLoading(true);
      
      const entryData = {
        companyId: currentCompany.id,
        date: formData.date,
        entryType: formData.entryType,
        description: formData.description,
        reference: formData.reference,
        lineItems: formData.lineItems.filter(item => 
          item.accountId && ((item.debit || 0) > 0 || (item.credit || 0) > 0)
        ),
        createdBy: user.uid,
        status: 'draft'
      };
      
      if (entry) {
        // Update existing entry
        await journalEntriesService.updateJournalEntry(entry.id, entryData);
        toast.success(t('accounting.updated_successfully'));
      } else {
        // Create new entry
        await journalEntriesService.addJournalEntry(entryData);
        toast.success(t('journal.created_successfully', 'Journal entry created successfully'));
      }
      
      onSuccess();
    } catch (error) {
      console.error('Error saving journal entry:', error);
      
      // Handle specific Firebase/network errors
      if (error.code === 'unavailable' || error.message.includes('network')) {
        toast.error('Network connection lost. Please check your internet connection and try again.');
      } else if (error.code === 'permission-denied') {
        toast.error('Permission denied. Please check your account permissions.');
      } else if (error.code === 'failed-precondition') {
        toast.error('Database index required. The journal entry was saved but may take a moment to appear. Please refresh the page.');
      } else if (error.message.includes('balance')) {
        toast.error('Journal entry validation failed: ' + error.message);
      } else if (error.message.includes('index')) {
        toast.error('Database configuration issue. Please contact support if this persists.');
      } else {
        toast.error(error.message || 'Failed to save journal entry. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Quick templates
  const applyTemplate = (templateType) => {
    let templateLineItems = [];
    
    switch (templateType) {
      case 'depreciation':
        // Try to find the correct accounts
        const depreciationExpenseAccount = chartOfAccounts.find(acc => acc.accountCode === '5600' || acc.accountName.includes('Depreciation Expense'));
        const accumulatedDepreciationAccount = chartOfAccounts.find(acc => acc.accountCode === '1700' || acc.accountName.includes('Accumulated Depreciation'));
        
        templateLineItems = [
          { 
            accountId: depreciationExpenseAccount?.id || '', 
            accountCode: depreciationExpenseAccount?.accountCode || '5600', 
            accountName: depreciationExpenseAccount?.accountName || 'Depreciation Expense', 
            debit: 1000, 
            credit: 0, 
            description: 'Monthly depreciation expense' 
          },
          { 
            accountId: accumulatedDepreciationAccount?.id || '', 
            accountCode: accumulatedDepreciationAccount?.accountCode || '1700', 
            accountName: accumulatedDepreciationAccount?.accountName || 'Accumulated Depreciation', 
            debit: 0, 
            credit: 1000, 
            description: 'Monthly depreciation accumulation' 
          }
        ];
        break;
      case 'accrual':
        // Try to find the correct accounts
        const expenseAccount = chartOfAccounts.find(acc => acc.accountCode === '5300' || acc.accountName.includes('Office Expenses'));
        const accruedExpensesAccount = chartOfAccounts.find(acc => acc.accountCode === '2200' || acc.accountName.includes('Accrued Expenses'));
        
        templateLineItems = [
          { 
            accountId: expenseAccount?.id || '', 
            accountCode: expenseAccount?.accountCode || '5300', 
            accountName: expenseAccount?.accountName || 'Office Expenses', 
            debit: 500, 
            credit: 0, 
            description: 'Accrued expense recognition' 
          },
          { 
            accountId: accruedExpensesAccount?.id || '', 
            accountCode: accruedExpensesAccount?.accountCode || '2200', 
            accountName: accruedExpensesAccount?.accountName || 'Accrued Expenses', 
            debit: 0, 
            credit: 500, 
            description: 'Accrued expense liability' 
          }
        ];
        break;
      case 'correction':
        // Provide empty template for corrections
        templateLineItems = [
          { accountId: '', accountCode: '', accountName: '', debit: 0, credit: 0, description: 'Correction entry - debit side' },
          { accountId: '', accountCode: '', accountName: '', debit: 0, credit: 0, description: 'Correction entry - credit side' }
        ];
        break;
      default:
        templateLineItems = [
          { accountId: '', accountCode: '', accountName: '', debit: 0, credit: 0, description: 'Line item' }
        ];
        break;
    }
    
    // Update form data
    setFormData(prev => ({
      ...prev,
      lineItems: templateLineItems,
      entryType: templateType,
      description: `${templateType.charAt(0).toUpperCase() + templateType.slice(1)} Entry - ${new Date().toLocaleDateString()}`
    }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[95vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b bg-gray-50">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {entry ? t('journal.edit_entry', 'Edit Journal Entry') : t('journal.new_entry', 'New Journal Entry')}
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                {t('journal.double_entry_desc', 'Create double-entry journal entries with proper accounting validation')}
              </p>
              {/* Network Status Indicator */}
              {networkStatus !== 'online' && (
                <div className={`mt-2 px-3 py-1 rounded-full text-xs font-medium inline-flex items-center space-x-1 ${
                  networkStatus === 'offline' ? 'bg-red-100 text-red-800' :
                  networkStatus === 'error' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-blue-100 text-blue-800'
                }`}>
                  <span>
                    {networkStatus === 'offline' ? '🔴' : 
                     networkStatus === 'error' ? '⚠️' : '🔄'}
                  </span>
                  <span>
                    {networkStatus === 'offline' ? 'Offline' :
                     networkStatus === 'error' ? 'Connection Issues' :
                     'Loading...'}
                  </span>
                </div>
              )}
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Quick Templates */}
            {!entry && (
              <div className="bg-blue-50 rounded-lg p-4">
                <h3 className="text-sm font-medium text-blue-900 mb-3">
                  {t('accounting.quick_templates')}
                </h3>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => applyTemplate('depreciation')}
                    className="btn btn-xs btn-outline"
                  >
                    📉 {t('accounting.depreciation')}
                  </button>
                  <button
                    type="button"
                    onClick={() => applyTemplate('accrual')}
                    className="btn btn-xs btn-outline"
                  >
                    📅 {t('journal.accrual', 'Accrual')}
                  </button>
                  <button
                    type="button"
                    onClick={() => applyTemplate('correction')}
                    className="btn btn-xs btn-outline"
                  >
                    🔧 {t('journal.correction', 'Correction')}
                  </button>
                </div>
              </div>
            )}

            {/* Basic Information */}
            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {t('accounting.basic_information', 'Basic Information')}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('chart.date')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  className={`input input-bordered w-full ${validationErrors.date ? 'input-error' : ''}`}
                  value={formData.date}
                  onChange={(e) => handleInputChange('date', e.target.value)}
                  required
                />
                {validationErrors.date && (
                  <p className="text-red-500 text-xs mt-1">{validationErrors.date}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('accounting.entry_type')} <span className="text-red-500">*</span>
                </label>
                <select
                  className={`select select-bordered w-full ${validationErrors.entryType ? 'select-error' : ''}`}
                  value={formData.entryType}
                  onChange={(e) => handleInputChange('entryType', e.target.value)}
                  required
                >
                  {entryTypes.map(type => (
                    <option key={type.id} value={type.id}>{type.name}</option>
                  ))}
                </select>
                {validationErrors.entryType && (
                  <p className="text-red-500 text-xs mt-1">{validationErrors.entryType}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('accounting.reference')}
                </label>
                <input
                  type="text"
                  className="input input-bordered w-full"
                  value={formData.reference}
                  onChange={(e) => handleInputChange('reference', e.target.value)}
                  placeholder={t('expenses.reference_placeholder')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('journal.balance_status', 'Balance Status')}
                </label>
                <div className={`p-3 rounded-lg border-2 text-sm font-semibold flex items-center space-x-2 ${
                  isBalanced 
                    ? 'bg-green-50 text-green-800 border-green-200' 
                    : 'bg-red-50 text-red-800 border-red-200'
                }`}>
                  <span className="text-lg">
                    {isBalanced ? '✅' : '⚠️'}
                  </span>
                  <div>
                    {isBalanced 
                      ? t('accounting.balanced')
                      : (
                        <div>
                          <div>{t('journal.not_balanced', 'Not Balanced')}</div>
                          <div className="text-xs font-normal">
                            {t('journal.difference', 'Difference')}: SAR {difference.toFixed(2)}
                          </div>
                        </div>
                      )
                    }
                  </div>
                </div>
              </div>
            </div>
          </div>

            {/* Description */}
            <div className="bg-white border rounded-lg p-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('accounting.description')} <span className="text-red-500">*</span>
              </label>
              <textarea
                className={`textarea textarea-bordered w-full resize-none ${
                  validationErrors.description ? 'textarea-error border-red-300' : 'border-gray-300'
                }`}
                rows={3}
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder={t('accounting.description_placeholder')}
                required
              />
              {validationErrors.description && (
                <p className="text-red-500 text-xs mt-1">{validationErrors.description}</p>
              )}
            </div>

            {/* Line Items */}
            <div>
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {t('accounting.line_items')} <span className="text-red-500">*</span>
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    {t('journal.line_items_desc', 'Add debit and credit entries to balance your journal entry')}
                  </p>
                </div>
                <div className="flex space-x-3">
                  {!isBalanced && formData.lineItems.length > 0 && (
                    <button
                      type="button"
                      onClick={autoBalance}
                      className="btn btn-sm btn-warning hover:btn-warning-focus flex items-center space-x-2"
                    >
                      <span>⚖️</span>
                      <span>{t('journal.auto_balance', 'Auto Balance')}</span>
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={addLineItem}
                    className="btn btn-sm btn-primary hover:btn-primary-focus flex items-center space-x-2"
                  >
                    <PlusIcon className="w-4 h-4" />
                    <span>{t('journal.add_line', 'Add Line')}</span>
                  </button>
                </div>
              </div>

              {/* Validation Summary */}
              {Object.keys(validationErrors).length > 0 && (
                <div className="alert alert-error mb-4">
                  <div>
                    <h4 className="font-semibold mb-2">Please fix the following errors:</h4>
                    <ul className="list-disc list-inside space-y-1 text-sm">
                      {validationErrors.date && <li>Date is required</li>}
                      {validationErrors.description && <li>Description is required</li>}
                      {validationErrors.entryType && <li>Entry type is required</li>}
                      {validationErrors.balance && <li>Entry must be balanced (debits must equal credits)</li>}
                      {validationErrors.lineItems && <li>At least one valid line item is required</li>}
                      {Object.keys(validationErrors).filter(key => key.includes('lineItem_')).length > 0 && (
                        <li>Some line items are missing required information (account or amount)</li>
                      )}
                    </ul>
                    {!isBalanced && (
                      <div className="mt-2 p-2 bg-yellow-50 rounded border border-yellow-200">
                        <strong>Balance Issue:</strong> Debits (SAR {totalDebits.toFixed(2)}) ≠ Credits (SAR {totalCredits.toFixed(2)})
                        <br />
                        <strong>Difference:</strong> SAR {difference.toFixed(2)}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Line Items - Improved Design */}
              <div className="bg-white border rounded-lg shadow-sm">
                {/* Table Header */}
                <div className="bg-gray-50 px-6 py-3 border-b">
                  <div className="grid grid-cols-12 gap-4 items-center font-medium text-gray-700">
                    <div className="col-span-4">{t('journal.account', 'Account')}</div>
                    <div className="col-span-3">{t('accounting.description')}</div>
                    <div className="col-span-2 text-center text-green-700">
                      <div className="flex items-center justify-center space-x-1">
                        <span>📈</span>
                        <span>{t('accounting.debit')}</span>
                      </div>
                    </div>
                    <div className="col-span-2 text-center text-red-700">
                      <div className="flex items-center justify-center space-x-1">
                        <span>📉</span>
                        <span>{t('accounting.credit')}</span>
                      </div>
                    </div>
                    <div className="col-span-1 text-center">{t('common.actions')}</div>
                  </div>
                </div>

                {/* Line Items */}
                <div className="divide-y divide-gray-100">
                  {formData.lineItems.length === 0 ? (
                    <div className="px-6 py-12 text-center">
                      <div className="text-gray-400 text-4xl mb-4">📝</div>
                      <h4 className="text-lg font-medium text-gray-900 mb-2">
                        {t('journal.no_line_items', 'No Line Items Added')}
                      </h4>
                      <p className="text-gray-600 mb-4">
                        {t('journal.add_line_items_desc', 'Add debit and credit entries to create your journal entry')}
                      </p>
                      <button
                        type="button"
                        onClick={addLineItem}
                        className="btn btn-primary"
                      >
                        <PlusIcon className="w-4 h-4 mr-2" />
                        {t('journal.add_first_line', 'Add First Line Item')}
                      </button>
                    </div>
                  ) : (
                    formData.lineItems.map((item, index) => (
                    <div key={index} className="px-6 py-4 hover:bg-gray-50 transition-colors">
                      <div className="grid grid-cols-12 gap-4 items-start">
                        {/* Account Selection */}
                        <div className="col-span-4">
                          <select
                            className={`select select-bordered w-full transition-colors ${
                              validationErrors[`lineItem_${index}_account`] 
                                ? 'select-error border-red-300' 
                                : item.accountId 
                                  ? 'border-blue-300 bg-blue-50' 
                                  : 'border-gray-300 hover:border-gray-400'
                            }`}
                            value={item.accountId}
                            onChange={(e) => handleLineItemChange(index, 'accountId', e.target.value)}
                          >
                            <option value="">{t('journal.select_account', 'Select Account')}</option>
                            {chartOfAccounts.map(account => (
                              <option key={account.id} value={account.id}>
                                {account.accountCode} - {i18n.language === 'ar' && account.accountNameAr ? account.accountNameAr : account.accountName}
                              </option>
                            ))}
                          </select>
                          {validationErrors[`lineItem_${index}_account`] && (
                            <p className="text-red-500 text-xs mt-1">{validationErrors[`lineItem_${index}_account`]}</p>
                          )}
                        </div>

                        {/* Description */}
                        <div className="col-span-3">
                          <input
                            type="text"
                            className={`input input-bordered w-full transition-colors ${
                              validationErrors[`lineItem_${index}_description`] 
                                ? 'input-error border-red-300' 
                                : item.description.trim() 
                                  ? 'border-blue-300 bg-blue-50' 
                                  : 'border-gray-300 hover:border-gray-400'
                            }`}
                            value={item.description}
                            onChange={(e) => handleLineItemChange(index, 'description', e.target.value)}
                            placeholder={t('journal.line_description_placeholder', 'Description for this line')}
                          />
                          {validationErrors[`lineItem_${index}_description`] && (
                            <p className="text-red-500 text-xs mt-1">{validationErrors[`lineItem_${index}_description`]}</p>
                          )}
                        </div>

                        {/* Debit Amount */}
                        <div className="col-span-2">
                          <div className="relative">
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              className={`input input-bordered w-full text-right font-mono ${
                                item.debit > 0 ? 'bg-green-50 border-green-300 text-green-800' : 'border-gray-300'
                              } ${validationErrors[`lineItem_${index}_amount`] ? 'input-error' : ''}`}
                              value={item.debit || ''}
                              onChange={(e) => handleLineItemChange(index, 'debit', e.target.value)}
                              placeholder="0.00"
                            />
                            {item.debit > 0 && (
                              <div className="absolute left-2 top-1/2 transform -translate-y-1/2 text-green-600 text-xs font-medium">
                                SAR
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Credit Amount */}
                        <div className="col-span-2">
                          <div className="relative">
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              className={`input input-bordered w-full text-right font-mono ${
                                item.credit > 0 ? 'bg-red-50 border-red-300 text-red-800' : 'border-gray-300'
                              } ${validationErrors[`lineItem_${index}_amount`] ? 'input-error' : ''}`}
                              value={item.credit || ''}
                              onChange={(e) => handleLineItemChange(index, 'credit', e.target.value)}
                              placeholder="0.00"
                            />
                            {item.credit > 0 && (
                              <div className="absolute left-2 top-1/2 transform -translate-y-1/2 text-red-600 text-xs font-medium">
                                SAR
                              </div>
                            )}
                          </div>
                          {validationErrors[`lineItem_${index}_amount`] && (
                            <p className="text-red-500 text-xs mt-1">{validationErrors[`lineItem_${index}_amount`]}</p>
                          )}
                        </div>

                        {/* Actions */}
                        <div className="col-span-1 flex justify-center">
                          <button
                            type="button"
                            onClick={() => removeLineItem(index)}
                            className="btn btn-sm btn-outline btn-error hover:bg-red-50"
                            disabled={formData.lineItems.length <= 1}
                            title={t('common.delete')}
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  )))}
                </div>

                {/* Totals Footer */}
                <div className="bg-gray-50 px-6 py-4 border-t">
                  <div className="grid grid-cols-12 gap-4 items-center">
                    <div className="col-span-7 flex items-center space-x-4">
                      <span className="font-semibold text-gray-900">{t('journal.totals', 'Totals')}:</span>
                      <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                        isBalanced 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {isBalanced 
                          ? `✅ ${t('accounting.balanced')}` 
                          : `⚠️ ${t('journal.difference', 'Difference')}: SAR ${difference.toFixed(2)}`
                        }
                      </div>
                    </div>
                    <div className="col-span-2 text-center">
                      <div className="bg-green-100 text-green-800 px-3 py-2 rounded-lg font-mono font-bold">
                        SAR {totalDebits.toFixed(2)}
                      </div>
                    </div>
                    <div className="col-span-2 text-center">
                      <div className="bg-red-100 text-red-800 px-3 py-2 rounded-lg font-mono font-bold">
                        SAR {totalCredits.toFixed(2)}
                      </div>
                    </div>
                    <div className="col-span-1"></div>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t bg-gray-50">
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-500">
              {isBalanced ? (
                <span className="text-green-600 font-medium">
                  ✅ {t('journal.entry_balanced', 'Entry is balanced and ready to save')}
                </span>
              ) : (
                <span className="text-red-600 font-medium">
                  ❌ {t('journal.entry_unbalanced', `Entry is not balanced. Difference: SAR ${difference.toFixed(2)}`)}
                </span>
              )}
            </div>
            <div className="flex space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="btn btn-outline"
                disabled={loading}
              >
                {t('common.cancel', t('common.cancel'))}
              </button>
              <button
                type="submit"
                onClick={handleSubmit}
                className="btn btn-primary"
                disabled={loading || !isBalanced}
              >
                {loading ? (
                  <LoadingSpinner size="sm" />
                ) : (
                  entry ? t('common.update', 'Update') : t('common.create', t('common.create'))
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateJournalEntryModal; 